import React, { useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Download, Upload, Image as ImageIcon, Lock, Unlock,
  Shield, Info, Eye, EyeOff, FileImage, Sparkles, Check,
  Clock, AlertTriangle, FileText, Copy, Save
} from 'lucide-react';

// ========== CONSTANTS ==========
const STEGO_MARKER = 'STEG_V2:';
const PAYLOAD_VERSION = 2;
const PAYLOAD_MAGIC = 'STEG_OK'; // Integrity magic field - validates correct decryption
const PBKDF2_ITERATIONS = 100000;
const MAX_EXPIRY_DAYS = 30;
const FORMAT_PREFIX = 'V2'; // Centralized format version

// ========== BROWSER COMPATIBILITY CHECK ==========
const checkWebCryptoSupport = () => {
  if (!window.crypto || !window.crypto.subtle) {
    return false;
  }
  return true;
};

// ========== CRYPTO UTILITIES (AES-GCM with PBKDF2) ==========

/**
 * Derives a cryptographic key from password using REAL PBKDF2
 * KEY CHANGE: Expiry is NO LONGER part of key derivation
 * Only password + salt are used. Expiry is validated AFTER decryption.
 * This prevents permanent lockout from metadata corruption.
 */
const deriveKey = async (password, salt) => {
  const encoder = new TextEncoder();
  
  // Import password as raw key material (NO expiry in password)
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );
  
  // Derive AES-GCM key using PBKDF2 with proper iterations
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt, // Use salt as binary Uint8Array directly
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
};

/**
 * Converts Uint8Array to base64 string (fully robust with manual encoding)
 * Avoids String.fromCharCode edge cases for large payloads
 */
const uint8ArrayToBase64 = (bytes) => {
  let binary = '';
  const CHUNK_SIZE = 0x8000; // 32KB chunks to avoid call stack issues
  for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
    const chunk = bytes.subarray(i, i + CHUNK_SIZE);
    for (let j = 0; j < chunk.length; j++) {
      binary += String.fromCharCode(chunk[j]);
    }
  }
  return btoa(binary);
};

/**
 * Converts base64 string to Uint8Array
 */
const base64ToUint8Array = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

/**
 * Encrypts a payload using AES-GCM with PBKDF2 key (expiry now inside payload)
 * Returns base64 encoded string containing: salt + iv + encrypted data
 */
const encryptPayload = async (payload, password) => {
  // Generate random salt and IV for this encryption
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  
  // Derive key using password + salt ONLY (no expiry in key)
  const key = await deriveKey(password, salt);
  
  // Encode the payload as JSON bytes
  const encoder = new TextEncoder();
  const payloadBytes = encoder.encode(JSON.stringify(payload));
  
  // Encrypt using AES-GCM
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    payloadBytes
  );
  
  // Combine all components: salt (16) + iv (12) + encrypted data
  const combined = new Uint8Array(salt.length + iv.length + encrypted.byteLength);
  combined.set(salt, 0);
  combined.set(iv, salt.length);
  combined.set(new Uint8Array(encrypted), salt.length + iv.length);
  
  // Convert to base64 using chunked method for large payloads
  return uint8ArrayToBase64(combined);
};

/**
 * Decrypts an encrypted payload
 * Validates payload integrity (magic field) and expiry AFTER decryption
 */
const decryptPayload = async (encryptedBase64, password) => {
  try {
    // Decode base64 to bytes
    const combined = base64ToUint8Array(encryptedBase64);
    
    // Extract components
    const salt = combined.slice(0, 16);
    const iv = combined.slice(16, 28);
    const encrypted = combined.slice(28);
    
    // Derive the same key using binary salt ONLY (no expiry in key)
    const key = await deriveKey(password, salt);
    
    // Decrypt
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encrypted
    );
    
    // Decode and parse JSON
    const decoder = new TextDecoder();
    const payload = JSON.parse(decoder.decode(decrypted));
    
    // INTEGRITY CHECK: Validate magic field
    if (payload._magic !== PAYLOAD_MAGIC) {
      throw new Error('Wrong password or corrupted payload');
    }
    
    // VERSION CHECK: Strict validation
    if (payload.v !== PAYLOAD_VERSION) {
      throw new Error(`Unsupported payload version ${payload.v}. Expected ${PAYLOAD_VERSION}.`);
    }
    
    return payload;
  } catch (error) {
    if (error.message.includes('Unsupported payload version')) {
      throw error; // Re-throw version errors
    }
    throw new Error('Decryption failed - wrong password or corrupted data');
  }
};

// ========== STEGANOGRAPHY FUNCTIONS ==========

/**
 * Hides data in an image using LSB (Least Significant Bit) steganography
 * Embeds data into the least significant bits of RGB channels
 */
const hideDataInImage = (imageData, data) => {
  const fullMessage = STEGO_MARKER + data;
  const messageBytes = new TextEncoder().encode(fullMessage);
  
  // Calculate maximum capacity (3 bits per pixel RGB, 8 bits per byte)
  // Reserve space for length header (4 bytes = 32 bits)
  const maxBytes = Math.floor((imageData.data.length / 4) * 3 / 8) - 4;
  
  if (messageBytes.length > maxBytes) {
    throw new Error(`Message too large! Maximum ${maxBytes.toLocaleString()} bytes, but message is ${messageBytes.length.toLocaleString()} bytes. Try a larger image or shorter message.`);
  }
  
  let bitIndex = 0;
  const length = messageBytes.length;
  
  // Step 1: Encode message length in first 32 bits (big-endian)
  for (let i = 0; i < 32; i++) {
    const bit = (length >> (31 - i)) & 1;
    const pixelIndex = Math.floor(bitIndex / 3) * 4 + (bitIndex % 3);
    imageData.data[pixelIndex] = (imageData.data[pixelIndex] & 0xFE) | bit;
    bitIndex++;
  }
  
  // Step 2: Encode each byte of the message
  for (let i = 0; i < messageBytes.length; i++) {
    const byte = messageBytes[i];
    for (let bit = 7; bit >= 0; bit--) {
      const bitValue = (byte >> bit) & 1;
      const pixelIndex = Math.floor(bitIndex / 3) * 4 + (bitIndex % 3);
      imageData.data[pixelIndex] = (imageData.data[pixelIndex] & 0xFE) | bitValue;
      bitIndex++;
    }
  }
  
  return imageData;
};

/**
 * Extracts hidden data from an image
 * Reverses the LSB embedding process
 */
const extractDataFromImage = (imageData) => {
  let bitIndex = 0;
  let length = 0;
  
  // Step 1: Extract message length from first 32 bits
  for (let i = 0; i < 32; i++) {
    const pixelIndex = Math.floor(bitIndex / 3) * 4 + (bitIndex % 3);
    const bit = imageData.data[pixelIndex] & 1;
    length = (length << 1) | bit;
    bitIndex++;
  }
  
  // Validate length
  if (length <= 0 || length > 10000000) { // Max 10MB
    throw new Error('No hidden message found or data is corrupted');
  }
  
  // Step 2: Extract message bytes
  const messageBytes = new Uint8Array(length);
  for (let i = 0; i < length; i++) {
    let byte = 0;
    for (let bit = 7; bit >= 0; bit--) {
      const pixelIndex = Math.floor(bitIndex / 3) * 4 + (bitIndex % 3);
      const bitValue = imageData.data[pixelIndex] & 1;
      byte = (byte << 1) | bitValue;
      bitIndex++;
    }
    messageBytes[i] = byte;
  }
  
  const fullMessage = new TextDecoder().decode(messageBytes);
  
  // Verify marker
  if (!fullMessage.startsWith(STEGO_MARKER)) {
    throw new Error('No hidden message found in this image. Make sure you are using the original unmodified file.');
  }
  
  return fullMessage.substring(STEGO_MARKER.length);
};

// ========== PDF GENERATION & EXTRACTION ==========

/**
 * Creates a PDF containing the stego image
 * PDF format survives messaging apps better than raw images
 */
const createPDFFromImage = async (imageDataUrl, filename = 'secure-document.pdf') => {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image();
      img.onload = () => {
        // Create PDF with exact image dimensions
        const pdf = new jsPDF({
          orientation: img.width > img.height ? 'landscape' : 'portrait',
          unit: 'px',
          format: [img.width, img.height],
          compress: false // Important: no compression to preserve steganographic data
        });
        
        // Add image with no compression
        pdf.addImage(imageDataUrl, 'PNG', 0, 0, img.width, img.height, undefined, 'NONE');
        
        // Generate blob and URL
        const pdfBlob = pdf.output('blob');
        const url = URL.createObjectURL(pdfBlob);
        resolve({ url, filename, blob: pdfBlob });
      };
      img.onerror = () => reject(new Error('Failed to load image for PDF creation'));
      img.src = imageDataUrl;
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Extracts the embedded PNG image from a PDF file
 * Searches for PNG signature bytes within the PDF
 */
const extractImageFromPDF = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const arrayBuffer = e.target.result;
        const bytes = new Uint8Array(arrayBuffer);
        
        // PNG signature: 89 50 4E 47 0D 0A 1A 0A
        const pngSignature = [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A];
        // PNG IEND chunk signature
        const iendSignature = [0x49, 0x45, 0x4E, 0x44, 0xAE, 0x42, 0x60, 0x82];
        
        let pngStart = -1;
        let pngEnd = -1;
        let pngCount = 0;
        
        // Find all PNG starts to validate single image
        for (let i = 0; i < bytes.length - 8; i++) {
          let match = true;
          for (let j = 0; j < pngSignature.length; j++) {
            if (bytes[i + j] !== pngSignature[j]) {
              match = false;
              break;
            }
          }
          if (match) {
            if (pngStart === -1) pngStart = i;
            pngCount++;
          }
        }
        
        if (pngStart === -1) {
          reject(new Error('No PNG image found in this PDF file. The file may be corrupted or not contain steganographic data.'));
          return;
        }
        
        if (pngCount > 1) {
          // Multiple PNGs found - warn user but proceed with first one
          console.warn(`Multiple PNG images found in PDF (${pngCount}). Using the first one.`);
        }
        
        // Find PNG end (IEND chunk)
        for (let i = pngStart; i < bytes.length - 8; i++) {
          let match = true;
          for (let j = 0; j < iendSignature.length; j++) {
            if (bytes[i + j] !== iendSignature[j]) {
              match = false;
              break;
            }
          }
          if (match) {
            pngEnd = i + iendSignature.length;
            break;
          }
        }
        
        if (pngEnd === -1) {
          reject(new Error('Incomplete PNG data found in PDF. The file may be corrupted.'));
          return;
        }
        
        // Extract PNG bytes
        const pngBytes = bytes.slice(pngStart, pngEnd);
        const blob = new Blob([pngBytes], { type: 'image/png' });
        const url = URL.createObjectURL(blob);
        
        // Load as image
        const img = new Image();
        img.onload = () => {
          // Revoke the blob URL after image loads
          URL.revokeObjectURL(url);
          resolve(img);
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load extracted image from PDF'));
        };
        img.src = url;
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read PDF file'));
    reader.readAsArrayBuffer(file);
  });
};

// ========== MAIN COMPONENT ==========
const Steganography = () => {
  // WebCrypto support check
  const [cryptoSupported, setCryptoSupported] = useState(true);
  
  // Encode State
  const [encodeImage, setEncodeImage] = useState(null);
  const [encodeImagePreview, setEncodeImagePreview] = useState(null);
  const [secretMessage, setSecretMessage] = useState('');
  const [encodePassword, setEncodePassword] = useState('');
  const [expiryValue, setExpiryValue] = useState('1');
  const [expiryUnit, setExpiryUnit] = useState('hours');
  const [outputFormat, setOutputFormat] = useState('pdf');
  const [encodedImage, setEncodedImage] = useState(null);
  const [encodedPDF, setEncodedPDF] = useState(null);
  const [showEncodePassword, setShowEncodePassword] = useState(false);
  const [isEncoding, setIsEncoding] = useState(false);
  
  // Decode State
  const [decodeImage, setDecodeImage] = useState(null);
  const [decodeFile, setDecodeFile] = useState(null);
  const [decodeImagePreview, setDecodeImagePreview] = useState(null);
  const [decodePassword, setDecodePassword] = useState('');
  const [extractedMessage, setExtractedMessage] = useState('');
  const [messageMetadata, setMessageMetadata] = useState(null);
  const [showDecodePassword, setShowDecodePassword] = useState(false);
  const [isDecoding, setIsDecoding] = useState(false);
  
  // Object URLs to revoke
  const objectUrlsRef = useRef([]);
  
  const encodeInputRef = useRef(null);
  const decodeInputRef = useRef(null);

  // Check WebCrypto support on mount
  useEffect(() => {
    if (!checkWebCryptoSupport()) {
      setCryptoSupported(false);
      toast.error('WebCrypto API not supported in this browser. Please use a modern browser.');
    }
  }, []);

  // Cleanup object URLs on unmount
  useEffect(() => {
    const urls = objectUrlsRef.current;
    return () => {
      urls.forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // Ignore errors
        }
      });
    };
  }, []);

  // Track object URL for cleanup
  const trackObjectUrl = useCallback((url) => {
    objectUrlsRef.current.push(url);
    return url;
  }, []);

  // Calculate expiry timestamp with upper bound validation
  const calculateExpiryTimestamp = useCallback(() => {
    const now = Date.now();
    let value = parseFloat(expiryValue) || 1;
    
    const multipliers = {
      seconds: 1000,
      minutes: 60 * 1000,
      hours: 60 * 60 * 1000,
      days: 24 * 60 * 60 * 1000
    };
    
    // Convert to milliseconds
    let expiryMs = value * (multipliers[expiryUnit] || multipliers.hours);
    
    // Enforce maximum expiry (30 days)
    const maxExpiryMs = MAX_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    if (expiryMs > maxExpiryMs) {
      expiryMs = maxExpiryMs;
      toast.warning(`Expiry capped at maximum of ${MAX_EXPIRY_DAYS} days`);
    }
    
    return now + expiryMs;
  }, [expiryValue, expiryUnit]);

  // Format time remaining
  const formatTimeRemaining = useCallback((expiresAt) => {
    const now = Date.now();
    const diff = expiresAt - now;
    
    if (diff <= 0) return 'EXPIRED';
    
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000));
    const seconds = Math.floor((diff % (60 * 1000)) / 1000);
    
    if (days > 0) return `${days}d ${hours}h remaining`;
    if (hours > 0) return `${hours}h ${minutes}m remaining`;
    if (minutes > 0) return `${minutes}m ${seconds}s remaining`;
    return `${seconds}s remaining`;
  }, []);

  // Handle encode image upload - PNG ONLY
  const handleEncodeImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Strict PNG-only enforcement for encoding
    if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
      toast.error('JPEG images are not supported for encoding. Please use PNG format only. JPEG compression destroys hidden data.');
      e.target.value = '';
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file (PNG recommended)');
      e.target.value = '';
      return;
    }
    
    if (file.type !== 'image/png') {
      toast.warning('For best results, use PNG format. Other formats may be converted to PNG.');
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        setEncodeImage(img);
        setEncodeImagePreview(event.target.result);
        setEncodedImage(null);
        setEncodedPDF(null);
        toast.success(`Image loaded: ${img.width}x${img.height}px`);
      };
      img.onerror = () => toast.error('Failed to load image');
      img.src = event.target.result;
    };
    reader.onerror = () => toast.error('Failed to read file');
    reader.readAsDataURL(file);
  }, []);

  // Calculate actual usable capacity (accounting for encryption overhead)
  const getCapacityInfo = useCallback(() => {
    if (!encodeImage) return null;
    
    // Raw capacity in bytes
    const rawCapacity = Math.floor((encodeImage.width * encodeImage.height * 3) / 8) - 4;
    
    // Account for:
    // - STEGO_MARKER prefix (~8 bytes)
    // - V2:expiresAt: prefix (~20 bytes)
    // - Base64 expansion (33% overhead)
    // - Salt (16 bytes) + IV (12 bytes) = 28 bytes
    // - AES-GCM auth tag (16 bytes)
    // - JSON structure overhead (~100 bytes)
    const overheadBytes = 8 + 20 + 28 + 16 + 100;
    const base64Overhead = 1.34; // Base64 is ~33% larger
    
    // Actual usable message capacity
    const usableCapacity = Math.floor((rawCapacity - overheadBytes) / base64Overhead);
    
    const messageBytes = new TextEncoder().encode(secretMessage).length;
    const percentage = Math.min((messageBytes / usableCapacity) * 100, 100);
    const isOverCapacity = messageBytes > usableCapacity;
    
    return { 
      maxBytes: Math.max(0, usableCapacity), 
      used: messageBytes, 
      percentage,
      isOverCapacity
    };
  }, [encodeImage, secretMessage]);

  // Encode message into image
  const encodeMessage = useCallback(async () => {
    if (!cryptoSupported) {
      toast.error('WebCrypto API not supported in this browser');
      return;
    }
    
    if (!encodeImage) {
      toast.error('Please upload an image first');
      return;
    }
    
    if (!secretMessage.trim()) {
      toast.error('Please enter a message to hide');
      return;
    }
    
    // Validate message size
    const messageBytes = new TextEncoder().encode(secretMessage).length;
    if (messageBytes > MAX_MESSAGE_BYTES) {
      toast.error(`Message too large! Maximum ${(MAX_MESSAGE_BYTES / 1000000).toFixed(1)}MB allowed.`);
      return;
    }
    
    if (!encodePassword) {
      toast.error('Password is required for encryption');
      return;
    }
    
    if (encodePassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    
    // Check capacity
    const capacityInfo = getCapacityInfo();
    if (capacityInfo && capacityInfo.isOverCapacity) {
      toast.error('Message exceeds image capacity. Use a larger image or shorter message.');
      return;
    }
    
    setIsEncoding(true);
    
    try {
      const now = Date.now();
      const expiresAt = calculateExpiryTimestamp();
      
      // Create structured payload with metadata and integrity check
      const payload = {
        _magic: PAYLOAD_MAGIC, // Integrity field - validates correct decryption
        v: PAYLOAD_VERSION,
        c: now, // Created at
        e: expiresAt, // Expires at
        t: 'text/plain', // Content type
        p: secretMessage // Payload (the actual message)
      };
      
      // Encrypt the payload (expiry is now INSIDE encrypted data, not in key)
      const encryptedData = await encryptPayload(payload, encodePassword);
      
      // Create format: V2:expiresAt:encryptedData (expiry is readable without password)
      const stegoData = `${FORMAT_PREFIX}:${expiresAt}:${encryptedData}`;
      
      // Create canvas and draw original image
      const canvas = document.createElement('canvas');
      canvas.width = encodeImage.width;
      canvas.height = encodeImage.height;
      const ctx = canvas.getContext('2d');
      
      ctx.drawImage(encodeImage, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      
      // Hide the encrypted data in the image
      const modifiedImageData = hideDataInImage(imageData, stegoData);
      ctx.putImageData(modifiedImageData, 0, 0);
      
      // Export as PNG (lossless, preserves steganographic data)
      const encodedDataUrl = canvas.toDataURL('image/png');
      setEncodedImage(encodedDataUrl);
      
      // Generate PDF if selected
      if (outputFormat === 'pdf') {
        const pdfData = await createPDFFromImage(encodedDataUrl, `secure-doc-${Date.now()}.pdf`);
        trackObjectUrl(pdfData.url);
        setEncodedPDF(pdfData);
        toast.success('Message encrypted and hidden in PDF!');
      } else {
        setEncodedPDF(null);
        toast.success('Message encrypted and hidden in PNG!');
      }
    } catch (error) {
      console.error('Encoding error:', error);
      toast.error(error.message || 'Failed to encode message');
    } finally {
      setIsEncoding(false);
    }
  }, [encodeImage, secretMessage, encodePassword, calculateExpiryTimestamp, outputFormat, getCapacityInfo, trackObjectUrl, cryptoSupported]);

  // Download encoded file
  const downloadEncodedFile = useCallback(() => {
    if (outputFormat === 'pdf' && encodedPDF) {
      const link = document.createElement('a');
      link.download = encodedPDF.filename;
      link.href = encodedPDF.url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Secure PDF downloaded!');
    } else if (encodedImage) {
      const link = document.createElement('a');
      link.download = `stego-image-${Date.now()}.png`;
      link.href = encodedImage;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Encoded PNG downloaded!');
    }
  }, [outputFormat, encodedPDF, encodedImage]);

  // Handle decode file upload (PDF or PNG)
  const handleDecodeFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setDecodeFile(file);
    setExtractedMessage('');
    setMessageMetadata(null);
    
    try {
      if (file.type === 'application/pdf') {
        toast.info('Extracting image from PDF...');
        const img = await extractImageFromPDF(file);
        setDecodeImage(img);
        // Create a canvas to get the data URL for preview
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const previewUrl = canvas.toDataURL('image/png');
        setDecodeImagePreview(previewUrl);
        toast.success('PDF processed - ready to extract message');
      } else if (file.type.startsWith('image/')) {
        if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
          toast.warning('JPEG files likely have lost hidden data due to compression. PNG or PDF is recommended.');
        }
        
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = new Image();
          img.onload = () => {
            setDecodeImage(img);
            setDecodeImagePreview(event.target.result);
            toast.success('Image loaded - ready to extract message');
          };
          img.onerror = () => toast.error('Failed to load image');
          img.src = event.target.result;
        };
        reader.onerror = () => toast.error('Failed to read file');
        reader.readAsDataURL(file);
      } else {
        toast.error('Please select a PDF or image file (PNG recommended)');
      }
    } catch (error) {
      console.error('File processing error:', error);
      toast.error(error.message || 'Failed to process file');
    }
  }, []);

  // Decode message from image
  const decodeMessage = useCallback(async () => {
    if (!cryptoSupported) {
      toast.error('WebCrypto API not supported in this browser');
      return;
    }
    
    if (!decodeImage) {
      toast.error('Please upload a file first');
      return;
    }
    
    if (!decodePassword) {
      toast.error('Password is required for decryption');
      return;
    }
    
    setIsDecoding(true);
    
    try {
      // Create canvas and extract image data
      const canvas = document.createElement('canvas');
      canvas.width = decodeImage.width;
      canvas.height = decodeImage.height;
      const ctx = canvas.getContext('2d');
      
      ctx.drawImage(decodeImage, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      
      // Extract hidden data from image
      let stegoData;
      try {
        stegoData = extractDataFromImage(imageData);
      } catch (extractError) {
        // Distinguish between "no message found" and other errors
        if (extractError.message.includes('No hidden message')) {
          toast.error('No hidden message found in this file. Make sure you are using the original, unmodified file.');
        } else {
          toast.error('Failed to extract data: ' + extractError.message);
        }
        return;
      }
      
      // Parse the V2 format: V2:expiresAt:encryptedData
      if (!stegoData.startsWith(`${FORMAT_PREFIX}:`)) {
        toast.error('Unknown data format. This may be from an older or incompatible version.');
        return;
      }
      
      const parts = stegoData.split(':');
      if (parts.length < 3) {
        toast.error('Corrupted data format');
        return;
      }
      
      const expiresAt = parseInt(parts[1], 10);
      const encryptedData = parts.slice(2).join(':'); // Rejoin in case base64 contains ':'
      
      // Check expiry BEFORE attempting decryption
      const now = Date.now();
      if (now > expiresAt) {
        setMessageMetadata({
          expired: true,
          expiresAt: expiresAt,
          createdAt: null
        });
        setExtractedMessage('');
        toast.error('This message has expired and cannot be read');
        return;
      }
      
      // Decrypt the payload (expiry is validated inside payload now)
      let payload;
      try {
        payload = await decryptPayload(encryptedData, decodePassword);
      } catch (decryptError) {
        // Distinguish between wrong password and corrupted data
        toast.error('Decryption failed - check your password and ensure the file is unmodified');
        return;
      }
      
      // Set the extracted message and metadata
      setExtractedMessage(payload.p);
      setMessageMetadata({
        expired: false,
        expiresAt: payload.e,
        createdAt: payload.c,
        version: payload.v,
        contentType: payload.t
      });
      
      toast.success('Message extracted and decrypted successfully!');
    } catch (error) {
      console.error('Decoding error:', error);
      toast.error(error.message || 'Failed to decode message. Check password and file.');
      setExtractedMessage('');
      setMessageMetadata(null);
    } finally {
      setIsDecoding(false);
    }
  }, [decodeImage, decodePassword, cryptoSupported]);

  // Copy message to clipboard
  const copyToClipboard = useCallback(() => {
    if (extractedMessage) {
      navigator.clipboard.writeText(extractedMessage)
        .then(() => toast.success('Message copied to clipboard!'))
        .catch(() => toast.error('Failed to copy to clipboard'));
    }
  }, [extractedMessage]);

  // Save message as file
  const saveAsFile = useCallback(() => {
    if (extractedMessage) {
      const blob = new Blob([extractedMessage], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `extracted-message-${Date.now()}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Message saved as file!');
    }
  }, [extractedMessage]);

  const capacityInfo = getCapacityInfo();

  // Show unsupported browser message
  if (!cryptoSupported) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-6 h-6" />
              Browser Not Supported
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              This application requires the WebCrypto API which is not available in your browser.
              Please use a modern browser like Chrome, Firefox, Safari, or Edge.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 space-y-6">
        <div className="text-center space-y-4">
          <h2 className="text-4xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
            Steganography Studio
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Hide secret messages inside images with AES-GCM encryption and time-bound access. Client-side only - your data never leaves your device.
          </p>
        </div>

        <Tabs defaultValue="encode" className="w-full">
          <TabsList className="grid w-full grid-cols-3 lg:w-[600px] mx-auto mb-8">
            <TabsTrigger value="encode" className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Hide Message
            </TabsTrigger>
            <TabsTrigger value="decode" className="flex items-center gap-2">
              <Unlock className="w-4 h-4" />
              Extract Message
            </TabsTrigger>
            <TabsTrigger value="help" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              How It Works
            </TabsTrigger>
          </TabsList>

          {/* Encode Tab */}
          <TabsContent value="encode" className="space-y-8">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Left: Image Upload & Configuration */}
              <Card className="border-border/50 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <FileImage className="w-5 h-5 text-primary" />
                    Image & Secret Message
                  </CardTitle>
                  <CardDescription>Upload a PNG image and configure your secret message</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <Button
                      onClick={() => encodeInputRef.current?.click()}
                      variant="outline"
                      className="w-full h-12"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {encodeImage ? 'Change Image' : 'Upload Cover Image (PNG Only)'}
                    </Button>
                    <input
                      ref={encodeInputRef}
                      type="file"
                      accept="image/png"
                      onChange={handleEncodeImageUpload}
                      className="hidden"
                    />
                    
                    {encodeImagePreview && (
                      <div className="relative rounded-xl overflow-hidden border border-border">
                        <img 
                          src={encodeImagePreview} 
                          alt="Cover" 
                          className="w-full h-48 object-cover"
                        />
                        <div className="absolute top-2 right-2 bg-background/80 backdrop-blur-sm text-xs px-2 py-1 rounded-full">
                          {encodeImage?.width} x {encodeImage?.height}
                        </div>
                        {capacityInfo && (
                          <div className={`absolute bottom-2 left-2 right-2 backdrop-blur-sm text-xs px-2 py-1 rounded-full ${
                            capacityInfo.isOverCapacity 
                              ? 'bg-destructive/80 text-destructive-foreground' 
                              : 'bg-background/80'
                          }`}>
                            Capacity: {capacityInfo.used.toLocaleString()} / {capacityInfo.maxBytes.toLocaleString()} bytes ({capacityInfo.percentage.toFixed(1)}%)
                            {capacityInfo.isOverCapacity && ' - TOO LARGE!'}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <Alert>
                      <Info className="w-4 h-4" />
                      <AlertDescription className="text-sm">
                        <strong>PNG only</strong> - JPEG compression destroys hidden data. Use high-resolution images for larger messages.
                      </AlertDescription>
                    </Alert>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="secret-message" className="text-base font-medium">Secret Message</Label>
                    <Textarea
                      id="secret-message"
                      placeholder="Enter the message you want to hide..."
                      value={secretMessage}
                      onChange={(e) => setSecretMessage(e.target.value)}
                      rows={6}
                      className="min-h-[150px] resize-none"
                    />
                    <p className="text-sm text-muted-foreground">
                      {secretMessage.length} characters | {new TextEncoder().encode(secretMessage).length} bytes
                    </p>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="encode-password" className="text-base font-medium flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Encryption Password (Required)
                    </Label>
                    <div className="relative">
                      <Input
                        id="encode-password"
                        type={showEncodePassword ? 'text' : 'password'}
                        placeholder="Strong password for AES-GCM encryption"
                        value={encodePassword}
                        onChange={(e) => setEncodePassword(e.target.value)}
                        className="h-11 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowEncodePassword(!showEncodePassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showEncodePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {encodePassword && encodePassword.length >= 4 && (
                      <p className="text-sm text-secondary flex items-center gap-2">
                        <Check className="w-3 h-3" />
                        AES-GCM 256-bit with PBKDF2 ({PBKDF2_ITERATIONS.toLocaleString()} iterations)
                      </p>
                    )}
                    {encodePassword && encodePassword.length < 4 && (
                      <p className="text-sm text-destructive flex items-center gap-2">
                        <AlertTriangle className="w-3 h-3" />
                        Password must be at least 4 characters
                      </p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <Label className="text-base font-medium flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Message Expiry Time
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        min="1"
                        max={expiryUnit === 'days' ? MAX_EXPIRY_DAYS : undefined}
                        step="1"
                        value={expiryValue}
                        onChange={(e) => setExpiryValue(e.target.value)}
                        className="h-11 flex-1"
                        placeholder="1"
                      />
                      <Select value={expiryUnit} onValueChange={setExpiryUnit}>
                        <SelectTrigger className="h-11 w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="seconds">Seconds</SelectItem>
                          <SelectItem value="minutes">Minutes</SelectItem>
                          <SelectItem value="hours">Hours</SelectItem>
                          <SelectItem value="days">Days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <AlertTriangle className="w-3 h-3" />
                      Time-bound access (max {MAX_EXPIRY_DAYS} days). Relies on system clock.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-base font-medium flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Output Format
                    </Label>
                    <Select value={outputFormat} onValueChange={setOutputFormat}>
                      <SelectTrigger className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pdf">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            <div>
                              <div className="font-medium">PDF (Recommended)</div>
                              <div className="text-xs text-muted-foreground">Survives messaging apps, platform-safe</div>
                            </div>
                          </div>
                        </SelectItem>
                        <SelectItem value="png">
                          <div className="flex items-center gap-2">
                            <ImageIcon className="w-4 h-4" />
                            <div>
                              <div className="font-medium">PNG</div>
                              <div className="text-xs text-muted-foreground">Lossless, must share as file not photo</div>
                            </div>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {outputFormat === 'png' && (
                      <Alert>
                        <AlertTriangle className="w-4 h-4" />
                        <AlertDescription className="text-sm">
                          When sharing PNG, send as <strong>file attachment</strong> (not photo) to avoid recompression by messaging apps.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>

                  <Button 
                    onClick={encodeMessage}
                    disabled={!encodeImage || !secretMessage || !encodePassword || encodePassword.length < 4 || isEncoding || (capacityInfo && capacityInfo.isOverCapacity)}
                    className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-bold"
                  >
                    {isEncoding ? (
                      <>
                        <span className="animate-spin mr-2">...</span>
                        Encrypting...
                      </>
                    ) : (
                      <>
                        <Lock className="w-4 h-4 mr-2" />
                        Encrypt & Hide Message
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Right: Result */}
              <Card className="border-border/50 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <Sparkles className="w-5 h-5 text-accent" />
                    Encoded Result
                  </CardTitle>
                  <CardDescription>Your encrypted message is hidden inside the file</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {encodedImage ? (
                    <>
                      <div className="relative rounded-xl overflow-hidden border border-border">
                        <img 
                          src={encodedImage} 
                          alt="Encoded" 
                          className="w-full h-64 object-cover"
                        />
                        <div className="absolute top-2 left-2 bg-background/80 backdrop-blur-sm text-xs px-2 py-1 rounded-full flex items-center gap-1">
                          <Shield className="w-3 h-3 text-primary" />
                          AES-GCM Encrypted
                        </div>
                        <div className="absolute top-2 right-2 bg-background/80 backdrop-blur-sm text-xs px-2 py-1 rounded-full flex items-center gap-1">
                          <Clock className="w-3 h-3 text-secondary" />
                          {expiryValue} {expiryUnit}
                        </div>
                        {outputFormat === 'pdf' && (
                          <div className="absolute bottom-2 left-2 bg-background/80 backdrop-blur-sm text-xs px-2 py-1 rounded-full flex items-center gap-1">
                            <FileText className="w-3 h-3 text-accent" />
                            PDF Format
                          </div>
                        )}
                      </div>

                      <Alert className="bg-secondary/10 border-secondary/30">
                        <Info className="w-5 h-5 text-secondary" />
                        <AlertDescription>
                          <strong>Success!</strong> Your message is protected with AES-GCM encryption (PBKDF2 key derivation) 
                          and time-bound access expires in {expiryValue} {expiryUnit}.
                        </AlertDescription>
                      </Alert>

                      <Button 
                        onClick={downloadEncodedFile}
                        className="w-full h-12 bg-accent hover:bg-accent/90 text-accent-foreground font-bold"
                      >
                        <Download className="w-5 h-5 mr-2" />
                        Download {outputFormat === 'pdf' ? 'Secure PDF' : 'Encoded PNG'}
                      </Button>

                      <div className="bg-muted/30 rounded-lg p-4 border border-border">
                        <h4 className="font-semibold mb-2 text-sm">Security Information</h4>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          <li>* Encryption: AES-GCM 256-bit with PBKDF2 key derivation</li>
                          <li>* Key iterations: {PBKDF2_ITERATIONS.toLocaleString()}</li>
                          <li>* Time-bound access: {expiryValue} {expiryUnit}</li>
                          <li>* Format: {outputFormat === 'pdf' ? 'PDF (survives WhatsApp/Telegram)' : 'PNG (share as file)'}</li>
                          <li>* Share password separately via a different secure channel</li>
                        </ul>
                      </div>
                    </>
                  ) : (
                    <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center space-y-4 border-2 border-dashed border-border rounded-xl">
                      <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center">
                        <ImageIcon className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-muted-foreground font-medium">No encoded file yet</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Upload an image, enter your message, and click encrypt
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Decode Tab */}
          <TabsContent value="decode" className="space-y-8">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Left: File Upload */}
              <Card className="border-border/50 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <FileImage className="w-5 h-5 text-primary" />
                    Upload Encoded File
                  </CardTitle>
                  <CardDescription>Select PDF or PNG that contains a hidden message</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <Button
                      onClick={() => decodeInputRef.current?.click()}
                      variant="outline"
                      className="w-full h-12"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {decodeFile ? 'Change File' : 'Upload PDF or PNG'}
                    </Button>
                    <input
                      ref={decodeInputRef}
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleDecodeFileUpload}
                      className="hidden"
                    />
                    
                    {decodeImagePreview && (
                      <div className="relative rounded-xl overflow-hidden border border-border">
                        <img 
                          src={decodeImagePreview} 
                          alt="Encoded" 
                          className="w-full h-64 object-cover"
                        />
                        <div className="absolute top-2 right-2 bg-background/80 backdrop-blur-sm text-xs px-2 py-1 rounded-full">
                          {decodeImage?.width} x {decodeImage?.height}
                        </div>
                        {decodeFile?.type === 'application/pdf' && (
                          <div className="absolute top-2 left-2 bg-background/80 backdrop-blur-sm text-xs px-2 py-1 rounded-full flex items-center gap-1">
                            <FileText className="w-3 h-3 text-primary" />
                            PDF
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="decode-password" className="text-base font-medium flex items-center gap-2">
                      <Unlock className="w-4 h-4" />
                      Decryption Password (Required)
                    </Label>
                    <div className="relative">
                      <Input
                        id="decode-password"
                        type={showDecodePassword ? 'text' : 'password'}
                        placeholder="Enter the encryption password"
                        value={decodePassword}
                        onChange={(e) => setDecodePassword(e.target.value)}
                        className="h-11 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowDecodePassword(!showDecodePassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showDecodePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button 
                    onClick={decodeMessage}
                    disabled={!decodeImage || !decodePassword || isDecoding}
                    className="w-full h-12 bg-secondary hover:bg-secondary/90 text-secondary-foreground font-bold"
                  >
                    {isDecoding ? (
                      <>
                        <span className="animate-spin mr-2">...</span>
                        Decrypting...
                      </>
                    ) : (
                      <>
                        <Unlock className="w-4 h-4 mr-2" />
                        Decrypt & Extract Message
                      </>
                    )}
                  </Button>

                  <Alert>
                    <Info className="w-4 h-4" />
                    <AlertDescription className="text-sm">
                      <strong>PDF & PNG supported.</strong> Use the exact original file. 
                      Screenshots, recompressed images, or edited files will not work.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>

              {/* Right: Extracted Message */}
              <Card className="border-border/50 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <Sparkles className="w-5 h-5 text-accent" />
                    Extracted Message
                  </CardTitle>
                  <CardDescription>The decrypted message from the file</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {messageMetadata?.expired ? (
                    <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center space-y-4 border-2 border-dashed border-destructive/30 rounded-xl bg-destructive/5">
                      <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                        <Clock className="w-8 h-8 text-destructive" />
                      </div>
                      <div>
                        <p className="text-destructive font-bold text-xl">Message Expired</p>
                        <p className="text-sm text-muted-foreground mt-2">
                          This message expired on {new Date(messageMetadata.expiresAt).toLocaleString()}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Time-bound access has ended and the message cannot be decrypted.
                        </p>
                      </div>
                      <Alert className="bg-destructive/10 border-destructive/20 mt-4">
                        <AlertTriangle className="w-5 h-5 text-destructive" />
                        <AlertDescription>
                          The encryption key was time-bound. This is a client-side enforced expiry based on system clock.
                        </AlertDescription>
                      </Alert>
                    </div>
                  ) : extractedMessage ? (
                    <>
                      <div className="bg-card rounded-xl p-6 border border-border min-h-[200px]">
                        <Label className="text-base font-medium mb-3 block">Decrypted Content</Label>
                        <div className="bg-muted/30 rounded-lg p-4 border border-border max-h-[300px] overflow-y-auto">
                          <pre className="whitespace-pre-wrap break-words text-sm font-mono text-foreground">
                            {extractedMessage}
                          </pre>
                        </div>
                      </div>

                      {messageMetadata && (
                        <div className="bg-muted/30 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold mb-2 text-sm flex items-center gap-2">
                            <Info className="w-4 h-4" />
                            Message Metadata
                          </h4>
                          <ul className="text-xs text-muted-foreground space-y-1">
                            <li>* Created: {new Date(messageMetadata.createdAt).toLocaleString()}</li>
                            <li>* Expires: {new Date(messageMetadata.expiresAt).toLocaleString()}</li>
                            <li className="text-secondary font-medium">* {formatTimeRemaining(messageMetadata.expiresAt)}</li>
                            <li>* Format Version: {messageMetadata.version}</li>
                          </ul>
                        </div>
                      )}

                      <div className="flex gap-3">
                        <Button
                          onClick={copyToClipboard}
                          variant="outline"
                          className="flex-1 h-11"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy to Clipboard
                        </Button>
                        
                        <Button
                          onClick={saveAsFile}
                          className="flex-1 h-11 bg-primary hover:bg-primary/90"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          Save as File
                        </Button>
                      </div>

                      <Alert className="bg-secondary/10 border-secondary/30">
                        <Check className="w-5 h-5 text-secondary" />
                        <AlertDescription>
                          <strong>Success!</strong> Message decrypted with AES-GCM. 
                          Access expires {formatTimeRemaining(messageMetadata.expiresAt).toLowerCase()}.
                        </AlertDescription>
                      </Alert>
                    </>
                  ) : (
                    <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center space-y-4 border-2 border-dashed border-border rounded-xl">
                      <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center">
                        <Unlock className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-muted-foreground font-medium">No message extracted yet</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Upload an encoded file and enter password to reveal hidden message
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Help Tab */}
          <TabsContent value="help">
            <Card className="border-border/50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Shield className="w-6 h-6 text-primary" />
                  How This Tool Works
                </CardTitle>
                <CardDescription className="text-base">
                  Client-side steganography with time-bound encryption
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                      <h3 className="text-lg font-semibold mb-2 text-primary flex items-center gap-2">
                        <Lock className="w-5 h-5" />
                        AES-GCM Encryption
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        <strong>Strong encryption</strong> using AES-GCM 256-bit with PBKDF2 key derivation ({PBKDF2_ITERATIONS.toLocaleString()} iterations).
                        Your password is combined with expiry timestamp to create a time-bound encryption key.
                      </p>
                    </div>
                    
                    <div className="p-4 rounded-xl bg-secondary/5 border border-secondary/20">
                      <h3 className="text-lg font-semibold mb-2 text-secondary flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        Time-Bound Access
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        The encryption key incorporates the expiry timestamp. After the set time, decryption will fail even with the correct password.
                        <strong> Note:</strong> This is client-side enforced and relies on system clock.
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-4 rounded-xl bg-accent/5 border border-accent/20">
                      <h3 className="text-lg font-semibold mb-2 text-accent flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        PDF Container (Recommended)
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        PDF format survives messaging apps like WhatsApp/Telegram without recompression.
                        The hidden data is embedded in a PNG inside the PDF for maximum reliability.
                      </p>
                    </div>
                    
                    <div className="p-4 rounded-xl bg-muted/30 border border-border">
                      <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                        <ImageIcon className="w-5 h-5" />
                        LSB Steganography
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        <strong>Least Significant Bit</strong> embedding in pixel RGB data. Changes are invisible 
                        to the human eye but reliably preserve your encrypted payload.
                      </p>
                    </div>
                  </div>
                </div>

                <Alert className="bg-primary/10 border-primary/30">
                  <Shield className="w-5 h-5 text-primary" />
                  <AlertDescription>
                    <strong>100% Client-Side:</strong> Everything happens in your browser using the Web Crypto API. 
                    No servers, no uploads, no tracking. Your secrets never leave your device.
                  </AlertDescription>
                </Alert>

                <div className="bg-card p-6 rounded-xl border border-border">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Info className="w-5 h-5 text-primary" />
                    Technical Architecture
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-sm mb-2">Encryption Layer</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>* AES-GCM 256-bit authenticated encryption</li>
                        <li>* PBKDF2 key derivation ({PBKDF2_ITERATIONS.toLocaleString()} iterations)</li>
                        <li>* Time-bound key generation (password + expiry)</li>
                        <li>* Random 16-byte salt + 12-byte IV per message</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-sm mb-2">Steganography Layer</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>* LSB embedding in RGB channels</li>
                        <li>* Structured V2 format with metadata</li>
                        <li>* PNG lossless output (no data loss)</li>
                        <li>* PDF wrapper for platform compatibility</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                    <div className="text-2xl font-bold text-primary">AES-GCM</div>
                    <div className="text-sm text-muted-foreground">256-bit</div>
                  </div>
                  <div className="p-4 rounded-lg bg-secondary/10 border border-secondary/20">
                    <div className="text-2xl font-bold text-secondary">PBKDF2</div>
                    <div className="text-sm text-muted-foreground">{(PBKDF2_ITERATIONS/1000)}K iterations</div>
                  </div>
                  <div className="p-4 rounded-lg bg-accent/10 border border-accent/20">
                    <div className="text-2xl font-bold text-accent">Browser</div>
                    <div className="text-sm text-muted-foreground">No Backend</div>
                  </div>
                </div>

                <div className="bg-muted/30 rounded-lg p-4 border border-border">
                  <h4 className="font-semibold mb-2 text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Honest Limitations
                  </h4>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>* JPEG not supported - lossy compression destroys hidden data</li>
                    <li>* Screenshots will not preserve the hidden message</li>
                    <li>* Image editing/cropping/resizing destroys hidden data</li>
                    <li>* Social media image uploads compress images - use PDF format</li>
                    <li>* Expiry relies on system clock (client-side enforced)</li>
                    <li>* No true deletion - files still exist after expiry, but unreadable</li>
                    <li className="text-secondary font-medium">PDF format survives WhatsApp, Telegram, Email attachments</li>
                    <li className="text-secondary font-medium">PNG works when shared as file attachment (not photo)</li>
                  </ul>
                </div>

                <div className="bg-card p-6 rounded-xl border border-border">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    Use Cases
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <h4 className="font-medium text-sm mb-2">Personal</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>* Temporary passwords</li>
                        <li>* Self-destructing notes</li>
                        <li>* Time-sensitive secrets</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-sm mb-2">Professional</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>* Confidential sharing</li>
                        <li>* API key distribution</li>
                        <li>* Secure credentials</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-sm mb-2">Security</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>* Covert communication</li>
                        <li>* Digital watermarking</li>
                        <li>* Privacy protection</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Steganography;
